import { useState } from 'react';
import type { ReactNode } from 'react';

export function stakkoInitials(name: string | null | undefined): string {
  const value = (name ?? '').trim();
  if (!value) return 'V';
  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export interface StakkoAvatarProps {
  name?: string | null;
  photoURL?: string | null;
  size?: number;
  className?: string;
}

export function StakkoAvatar({ name, photoURL, size = 36, className }: StakkoAvatarProps) {
  const [failed, setFailed] = useState(false);
  const dimension = `${size}px`;

  if (photoURL && !failed) {
    return (
      <img
        src={photoURL}
        alt={name ?? 'Uživatel'}
        width={size}
        height={size}
        referrerPolicy="no-referrer"
        className={['stakko-avatar-img', className].filter(Boolean).join(' ')}
        style={{ width: dimension, height: dimension }}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span
      className={['stakko-avatar-initials', className].filter(Boolean).join(' ')}
      style={{ width: dimension, height: dimension, fontSize: Math.round(size * 0.36) }}
      aria-hidden="true"
    >
      {stakkoInitials(name)}
    </span>
  );
}

export function StakkoBadge({
  children,
  tone = 'brand',
}: {
  children: ReactNode;
  tone?: 'brand' | 'muted' | 'success' | 'warning';
}) {
  return <span className={`stakko-badge stakko-badge-${tone}`}>{children}</span>;
}

export function StakkoDivider({ label }: { label?: string }) {
  if (!label) return <div className="stakko-divider" role="separator" />;
  return (
    <div className="stakko-divider-label" role="separator">
      <span>{label}</span>
    </div>
  );
}

export function StakkoEmpty({
  icon,
  title,
  body,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
}) {
  return (
    <div className="stakko-empty">
      {icon ? <div className="stakko-empty-icon">{icon}</div> : null}
      <strong>{title}</strong>
      {body ? <p>{body}</p> : null}
    </div>
  );
}

export function StakkoSpinner({ label = 'Načítám…' }: { label?: string }) {
  return (
    <div className="stakko-spinner" role="status" aria-live="polite">
      <span className="stakko-spinner-dot" />
      <span>{label}</span>
    </div>
  );
}
